import { ReactNode } from 'react';
import { TEXT } from './Typography';

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  onClick?: () => void;
}

export default function Card({
  children,
  className = '',
  padding = 'md',
  shadow = 'sm',
  hover = false,
  onClick,
}: CardProps) {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8',
  };

  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow',
    lg: 'shadow-lg',
  };

  const interactiveClasses =
    onClick || hover ? 'cursor-pointer transition-shadow hover:shadow-md' : '';

  // A caller's own background or border colour must win. Both used to be
  // emitted alongside the defaults, and Tailwind's stylesheet order — not the
  // class order — decided, so a tinted card silently rendered white.
  const background = /(^|\s)bg-/.test(className) ? '' : 'bg-white';
  const borderColour = /(^|\s)border-(?:[a-z]+-\d{2,3}|white|black|transparent)(\s|$)/.test(className)
    ? ''
    : 'border-gray-200';

  return (
    <div
      className={`${background} rounded-lg border ${borderColour} ${paddingClasses[padding]} ${shadowClasses[shadow]} ${interactiveClasses} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
  /**
   * Adds the card's own inset to the header. Needed when the parent `Card` uses
   * `padding="none"` — typically because its body holds a full-bleed table — as
   * the header would otherwise have no top or side padding and its actions
   * would sit hard against the card's corner.
   */
  padded?: boolean;
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className = '',
  children,
  padded = false,
}: CardHeaderProps) {
  const inset = padded ? 'px-4 pt-4 sm:px-6 sm:pt-5' : '';

  // If children are provided, render them directly (custom header)
  if (children) {
    return <div className={`${inset} pb-4 border-b border-gray-200 ${className}`}>{children}</div>;
  }

  // Standard header with title/subtitle/actions. Wraps to two rows on narrow
  // viewports so a long title and its actions don't fight for the same line.
  return (
    <div
      className={`${inset} pb-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${className}`}
    >
      <div className="flex-1 min-w-0">
        {title && <h3 className={`leading-snug ${TEXT.sectionTitle}`}>{title}</h3>}
        {subtitle && <p className="text-base text-gray-600 mt-1 leading-relaxed">{subtitle}</p>}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 sm:ml-4 sm:justify-end shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}

interface CardBodyProps {
  children: ReactNode;
  className?: string;
}

export function CardBody({ children, className = '' }: CardBodyProps) {
  return <div className={`py-4 ${className}`}>{children}</div>;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
  return (
    <div
      className={`pt-4 border-t border-gray-200 flex items-center justify-end gap-2 ${className}`}
    >
      {children}
    </div>
  );
}
